/**
 * Beta 模式權限中介層
 *
 * 沿用 permissions.js 的模式，為 Project / Ticket 提供權限檢查
 */

import { isSuperAdmin, isDepartmentAdmin } from './permissions';

const isCompanyAdmin = (profile) => {
  return profile?.role === 'company_admin' || profile?.role === 'super_admin';
};

// ========================================
// Project 權限
// ========================================

/**
 * 檢查是否可以查看 Project
 * Project 是公司層級共享容器，同公司就能看
 */
export const canViewProject = (profile, project) => {
  if (!profile || !project) return false;
  if (isSuperAdmin(profile)) return true;
  if (project.owner_id === profile.id) return true;
  return project.company_id === profile.company_id;
};

/**
 * 檢查是否可以建立 Project
 * 僅 super_admin / company_admin 可建立
 */
export const canCreateProject = (profile) => {
  if (!profile) return false;
  return isSuperAdmin(profile) || isCompanyAdmin(profile);
};

/**
 * 檢查是否可以編輯 Project
 * 僅 super_admin / company_admin 可編輯
 */
export const canEditProject = (profile, project) => {
  if (!profile || !project) return false;
  return isSuperAdmin(profile) || isCompanyAdmin(profile);
};

/**
 * 檢查是否可以刪除 Project
 * 僅 super_admin / company_admin 可刪除
 */
export const canDeleteProject = (profile, project) => {
  if (!profile || !project) return false;
  return isSuperAdmin(profile) || isCompanyAdmin(profile);
};

// ========================================
// Ticket 權限
// ========================================

/**
 * 檢查是否可以查看 Ticket
 */
export const canViewTicket = (profile, ticket, project = null) => {
  if (!profile || !ticket) return false;

  if (isSuperAdmin(profile)) return true;

  // Creator
  if (ticket.creator_id === profile.id) return true;

  // Assignee
  if (ticket.ticket_assignees?.some((a) => a.user_id === profile.id)) return true;

  // Inherits from project
  if (project) {
    return canViewProject(profile, project);
  }

  // Same department
  if (ticket.department_id === profile.department_id) return true;

  return false;
};

/**
 * 檢查是否可以編輯 Ticket
 * 同公司的人都能編輯 Ticket（公司層級協作）
 */
export const canEditTicket = (profile, ticket, project = null) => {
  if (!profile || !ticket) return false;

  if (isSuperAdmin(profile)) return true;

  // Company admin 可以編輯同公司的 ticket
  if (isCompanyAdmin(profile) && ticket.company_id === profile.company_id) return true;

  // Creator
  if (ticket.creator_id === profile.id) return true;

  // Assignee
  if (ticket.ticket_assignees?.some((a) => a.user_id === profile.id)) return true;

  // Department admin
  if (isDepartmentAdmin(profile) && ticket.department_id === profile.department_id) return true;

  // 同公司成員也可以編輯（協作精神）
  if (ticket.company_id === profile.company_id) return true;

  return false;
};

/**
 * 檢查是否可以刪除 Ticket
 */
export const canDeleteTicket = (profile, ticket) => {
  if (!profile || !ticket) return false;

  if (isSuperAdmin(profile)) return true;
  if (ticket.creator_id === profile.id) return true;

  return false;
};

// ========================================
// Comment 權限
// ========================================

/**
 * 檢查是否可以編輯留言
 */
export const canEditComment = (profile, comment) => {
  if (!profile || !comment) return false;

  if (isSuperAdmin(profile)) return true;
  if (comment.author_id === profile.id) return true;

  return false;
};

/**
 * 檢查是否可以刪除留言
 */
export const canDeleteComment = (profile, comment) => {
  return canEditComment(profile, comment);
};

// ========================================
// Delivery Log 權限
// ========================================

/**
 * 檢查是否可以編輯交付紀錄
 * 僅作者本人或 super_admin
 */
export const canEditDeliveryLog = (profile, log) => {
  if (!profile || !log) return false;
  if (isSuperAdmin(profile)) return true;
  return log.user_id === profile.id;
};

/**
 * 檢查是否可以刪除交付紀錄
 */
export const canDeleteDeliveryLog = (profile, log) => {
  return canEditDeliveryLog(profile, log);
};

// ========================================
// Work Item 權限(比照 Ticket:公司層級協作)
// ========================================

/**
 * 檢查是否可以查看工作項目
 */
export const canViewWorkItem = (profile, workItem, project = null) => {
  if (!profile || !workItem) return false;
  if (isSuperAdmin(profile)) return true;
  if (workItem.creator_id === profile.id) return true;
  if (workItem.assignees?.some((a) => a.user_id === profile.id)) return true;
  if (project) return canViewProject(profile, project);
  if (workItem.department_id === profile.department_id) return true;
  return false;
};

/**
 * 檢查是否可以編輯工作項目(同公司成員即可,協作精神)
 */
export const canEditWorkItem = (profile, workItem) => {
  if (!profile || !workItem) return false;
  if (isSuperAdmin(profile)) return true;
  if (isCompanyAdmin(profile) && workItem.company_id === profile.company_id) return true;
  if (workItem.creator_id === profile.id) return true;
  if (workItem.assignees?.some((a) => a.user_id === profile.id)) return true;
  if (isDepartmentAdmin(profile) && workItem.department_id === profile.department_id) return true;
  if (workItem.company_id === profile.company_id) return true;
  return false;
};

/**
 * 檢查是否可以刪除工作項目
 * 僅 super_admin / 建立者(工作項目一般不硬刪,以狀態收掉)
 */
export const canDeleteWorkItem = (profile, workItem) => {
  if (!profile || !workItem) return false;
  if (isSuperAdmin(profile)) return true;
  if (workItem.creator_id === profile.id) return true;
  return false;
};

/**
 * 檢查是否可以管理群組(建立/改名/刪除/排序)
 * 群組是專案內的組織容器,比照工作項目採「同公司協作」:任何登入成員皆可管理。
 * (刪除已另限制只能刪空群組;改名/排序無破壞性)
 */
export const canManageWorkItemGroup = (profile) => !!profile;

// ========================================
// 統一權限檢查
// ========================================

export const checkBetaPermission = (action, resource, profile, context = {}) => {
  if (!profile) return false;

  const [resourceType, actionType] = action.split('.');

  switch (resourceType) {
    case 'project':
      if (actionType === 'create') return canCreateProject(profile);
      if (actionType === 'view') return canViewProject(profile, resource);
      if (actionType === 'edit') return canEditProject(profile, resource);
      if (actionType === 'delete') return canDeleteProject(profile, resource);
      break;

    case 'ticket':
      if (actionType === 'view') return canViewTicket(profile, resource, context.project);
      if (actionType === 'edit') return canEditTicket(profile, resource, context.project);
      if (actionType === 'delete') return canDeleteTicket(profile, resource);
      break;

    case 'work_item':
      if (actionType === 'view') return canViewWorkItem(profile, resource, context.project);
      if (actionType === 'edit') return canEditWorkItem(profile, resource);
      if (actionType === 'delete') return canDeleteWorkItem(profile, resource);
      break;

    case 'work_item_group':
      if (actionType === 'manage') return canManageWorkItemGroup(profile);
      break;

    case 'comment':
      if (actionType === 'edit') return canEditComment(profile, resource);
      if (actionType === 'delete') return canDeleteComment(profile, resource);
      break;

    case 'delivery_log':
      if (actionType === 'edit') return canEditDeliveryLog(profile, resource);
      if (actionType === 'delete') return canDeleteDeliveryLog(profile, resource);
      break;
  }

  return false;
};
